import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { formatDistanceToNow } from "date-fns";
import { MessageCircle, Send, Trash2, User, SmilePlus, Eye, Edit3 } from "lucide-react";
import useCollaboration from "@/hooks/useCollaboration";
import { FoundingBadge } from "@/components/FoundingBadge";

const REACTION_EMOJIS = ["❤️", "🎉", "✈️", "🔥", "👏"];

const ReactionButton = ({ comment, userId, onReactionToggle }) => {
  const [isOpen, setIsOpen] = useState(false);
  const reactions = comment.reactions || [];
  
  const handleReaction = async (emoji) => {
    setIsOpen(false);
    onReactionToggle(comment.id, emoji);
  };
  
  // Check if current user has reacted with each emoji
  const getUserReaction = (emoji) => {
    const reaction = reactions.find(r => r.emoji === emoji);
    return reaction?.users?.includes(userId);
  };
  
  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button
          className="text-muted-foreground hover:text-primary transition-colors p-1 rounded-full hover:bg-primary/10"
          data-testid={`reaction-trigger-${comment.id}`}
        >
          <SmilePlus className="w-3.5 h-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-2" align="start">
        <div className="flex gap-1">
          {REACTION_EMOJIS.map((emoji) => (
            <button
              key={emoji}
              onClick={() => handleReaction(emoji)}
              className={`text-lg p-1.5 rounded-full transition-all hover:scale-125 ${
                getUserReaction(emoji) 
                  ? "bg-primary/20 ring-2 ring-primary/40" 
                  : "hover:bg-muted"
              }`}
              data-testid={`reaction-${emoji}-${comment.id}`}
            >
              {emoji}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const ReactionDisplay = ({ reactions, userId }) => {
  if (!reactions || reactions.length === 0) return null;
  
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {reactions.map((reaction) => {
        const userReacted = reaction.users?.includes(userId);
        const names = reaction.user_names?.join(", ") || "";
        
        return (
          <div
            key={reaction.emoji}
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs ${
              userReacted 
                ? "bg-primary/20 text-primary border border-primary/30" 
                : "bg-muted text-muted-foreground"
            }`}
            title={names}
          >
            <span>{reaction.emoji}</span>
            <span className="font-medium">{reaction.count}</span>
          </div>
        );
      })}
    </div>
  );
};

export const CommentsPanel = ({ bucketItemId, bucketItemTitle, isOpen, onClose }) => {
  const { user, token } = useAuth();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const scrollRef = useRef(null);

  // Collaboration hook
  const {
    connected,
    joinItem,
    leaveItem,
    startTyping,
    stopTyping,
    getViewers,
    getTypingUsers,
  } = useCollaboration(token, user?.name);

  // Get current viewers and typing users
  const viewers = getViewers(bucketItemId);
  const typingUsers = getTypingUsers(bucketItemId);

  // Join/leave item room when panel opens/closes
  useEffect(() => {
    if (isOpen && bucketItemId && connected) {
      joinItem(bucketItemId);
    }
    
    return () => {
      if (bucketItemId) {
        leaveItem(bucketItemId);
      }
    };
  }, [isOpen, bucketItemId, connected, joinItem, leaveItem]);

  // Handle typing with debounce
  const handleTyping = useCallback(() => {
    if (bucketItemId) {
      startTyping(bucketItemId, 'comment');
    }
  }, [bucketItemId, startTyping]);

  const fetchComments = async () => {
    try {
      const res = await axios.get(`${API}/items/${bucketItemId}/comments`);
      setComments(res.data);
    } catch (error) {
      console.error("Failed to fetch comments");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && bucketItemId) {
      fetchComments();
    }
  }, [isOpen, bucketItemId]);

  useEffect(() => {
    // Scroll to bottom when new comments are added
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [comments]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    setSubmitting(true);
    try {
      await axios.post(`${API}/items/${bucketItemId}/comments`, {
        text: newComment.trim()
      });
      setNewComment("");
      fetchComments();
    } catch (error) {
      toast.error("Failed to add comment");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (commentId) => {
    try {
      await axios.delete(`${API}/comments/${commentId}`);
      setComments(comments.filter(c => c.id !== commentId));
      toast.success("Comment deleted");
    } catch (error) {
      toast.error("Failed to delete comment");
    }
  };

  const handleReactionToggle = async (commentId, emoji) => {
    try {
      const res = await axios.post(`${API}/comments/${commentId}/reactions`, { emoji });
      
      // Optimistic update: refresh reactions
      const reactionsRes = await axios.get(`${API}/comments/${commentId}/reactions`);
      
      setComments(prevComments => 
        prevComments.map(c => 
          c.id === commentId 
            ? { ...c, reactions: reactionsRes.data }
            : c
        )
      );
      
      if (res.data.action === "added") {
        // Optional: subtle feedback
      }
    } catch (error) {
      toast.error("Failed to react");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-card border-none rounded-2xl p-0 max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader className="p-6 pb-4 border-b border-charcoal/5">
          <DialogTitle className="font-serif text-xl text-charcoal flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-primary" />
            Comments
          </DialogTitle>
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">{bucketItemTitle}</p>
            
            {/* Collaboration Indicator */}
            {viewers.length > 0 && (
              <div className="flex items-center gap-2 px-2 py-1 bg-primary/5 rounded-full" data-testid="viewers-indicator">
                <Eye className="w-3.5 h-3.5 text-primary" />
                <div className="flex -space-x-1">
                  {viewers.slice(0, 3).map((v) => (
                    <div
                      key={v.id || v.name || Math.random()}
                      className="w-5 h-5 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white text-[10px] font-semibold ring-1 ring-white"
                      title={v.name}
                    >
                      {(v.name || 'U')[0].toUpperCase()}
                    </div>
                  ))}
                  {viewers.length > 3 && (
                    <div className="w-5 h-5 rounded-full bg-charcoal/20 flex items-center justify-center text-charcoal text-[10px] font-semibold ring-1 ring-white">
                      +{viewers.length - 3}
                    </div>
                  )}
                </div>
                <span className="text-xs text-primary font-medium">viewing</span>
              </div>
            )}
          </div>
        </DialogHeader>

        {/* Comments List */}
        <ScrollArea className="flex-1 max-h-[50vh]" ref={scrollRef}>
          <div className="p-6 space-y-4">
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">Loading...</div>
            ) : comments.length === 0 ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                  <MessageCircle className="w-8 h-8 text-muted-foreground" />
                </div>
                <h3 className="font-medium text-charcoal mb-2">No comments yet</h3>
                <p className="text-sm text-muted-foreground">
                  Start the conversation with your partner
                </p>
              </div>
            ) : (
              comments.map((comment) => (
                <div
                  key={comment.id}
                  className={`flex gap-3 ${comment.created_by === user.id ? "flex-row-reverse" : ""}`}
                  data-testid={`comment-${comment.id}`}
                >
                  {/* Avatar with profile picture */}
                  <div className="w-8 h-8 rounded-full flex-shrink-0 overflow-hidden">
                    {comment.profile_picture_path ? (
                      <img 
                        src={`${API}/profile/picture/${comment.profile_picture_path}`}
                        alt={comment.created_by_name}
                        className="w-8 h-8 rounded-full object-cover"
                        onError={(e) => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }}
                      />
                    ) : comment.avatar_url ? (
                      <img 
                        src={comment.avatar_url}
                        alt={comment.created_by_name}
                        className="w-8 h-8 rounded-full object-cover"
                        onError={(e) => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }}
                      />
                    ) : null}
                    <div className={`w-8 h-8 rounded-full bg-muted items-center justify-center ${comment.profile_picture_path || comment.avatar_url ? 'hidden' : 'flex'}`}>
                      <User className="w-4 h-4 text-muted-foreground" />
                    </div>
                  </div>
                  
                  <div className={`flex-1 max-w-[80%] ${comment.created_by === user.id ? "text-right" : ""}`}>
                    <div
                      className={`inline-block rounded-2xl px-4 py-2 ${
                        comment.created_by === user.id
                          ? "bg-primary text-white rounded-tr-sm"
                          : "bg-muted text-charcoal rounded-tl-sm"
                      }`}
                    >
                      <p className="text-sm">{comment.text}</p>
                    </div>
                    
                    {/* Reactions Display */}
                    <ReactionDisplay reactions={comment.reactions} userId={user.id} />
                    
                    <div className={`flex items-center gap-2 mt-1 ${comment.created_by === user.id ? "justify-end" : ""}`}>
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        {comment.created_by_name}
                        {comment.founding_explorer && (
                          <FoundingBadge 
                            size="xs" 
                            founderNumber={comment.founding_explorer_number}
                            className="h-4 w-4"
                          />
                        )}
                        <span>•</span> {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                      </span>
                      
                      {/* Reaction Button */}
                      <ReactionButton 
                        comment={comment} 
                        userId={user.id} 
                        onReactionToggle={handleReactionToggle}
                      />
                      
                      {comment.created_by === user.id && (
                        <button
                          onClick={() => handleDelete(comment.id)}
                          className="text-muted-foreground hover:text-destructive transition-colors"
                          data-testid={`delete-comment-${comment.id}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </ScrollArea>

        {/* Comment Input */}
        <form onSubmit={handleSubmit} className="p-4 border-t border-charcoal/5 bg-background">
          {/* Typing Indicator */}
          {typingUsers.length > 0 && (
            <div className="flex items-center gap-2 text-muted-foreground text-xs italic mb-2" data-testid="typing-indicator">
              <div className="flex gap-1">
                <span className="w-1.5 h-1.5 bg-secondary rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-1.5 h-1.5 bg-secondary rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-1.5 h-1.5 bg-secondary rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
              <span>
                {typingUsers.length === 1 
                  ? `${typingUsers[0].name} is typing...`
                  : `${typingUsers.map(u => u.name).join(', ')} are typing...`
                }
              </span>
            </div>
          )}
          
          <div className="flex gap-2">
            <Textarea
              placeholder="Write a comment..."
              value={newComment}
              onChange={(e) => {
                setNewComment(e.target.value);
                handleTyping();
              }}
              onBlur={() => stopTyping(bucketItemId)}
              className="min-h-[44px] max-h-[120px] resize-none bg-card border-charcoal/10 rounded-xl"
              rows={1}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  stopTyping(bucketItemId);
                  handleSubmit(e);
                }
              }}
              data-testid="comment-input"
            />
            <Button
              type="submit"
              disabled={submitting || !newComment.trim()}
              className="h-11 w-11 rounded-xl bg-primary text-white hover:bg-primary/90 p-0"
              data-testid="send-comment-btn"
            >
              <Send className="w-5 h-5" />
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Press Enter to send • Click 😊 to react
            {connected && <span className="ml-2 text-green-500">• Live</span>}
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CommentsPanel;
